import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WatchlistItemDto } from '@rhcst/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { PairSymbols } from '../components/PairRow.js';
import { RiskBadge } from '../components/RiskBadge.js';
import { shortAddress, formatEth, relativeAge, absoluteTime } from '../lib/format.js';

/**
 * Watchlist.
 *
 * Pairs the operator has flagged by hand. Kept as its own view rather than a
 * filter on the feed, because a watchlist is a decision the operator made and a
 * feed is a stream that arrives — mixing them would bury the former in the
 * latter.
 *
 * v2 adds a Risk column (score/verdict, the same `RiskBadge` the feed uses)
 * and a Lifecycle column — previously an operator had to click into every
 * watched pair to know whether anything had changed.
 */

export function Watchlist() {
  const [items, setItems] = useState<WatchlistItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<{ items: WatchlistItemDto[] }>('/watchlist');
      setItems(result.items);
      setError(null);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load the watchlist.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(item: WatchlistItemDto) {
    setBusyId(item.id);
    try {
      await api.delete(`/watchlist/${item.id}`);
      setItems((current) => current.filter((row) => row.id !== item.id));
    } catch {
      setError('Could not remove that item.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="shell-content">
      {error !== null && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Watchlist</h2>
          <span className="legend">
            {items.length} pair{items.length === 1 ? '' : 's'}
          </span>
        </div>

        {loading ? (
          <div className="panel-body">
            <p className="panel-note">Loading watchlist…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <p className="empty-title">Nothing on the watchlist</p>
            <p className="empty-body">
              Add a pair from the feed or from its detail page to keep it here.
            </p>
            <p>
              <Link className="back-link" to="/pairs">
                Go to the pair feed →
              </Link>
            </p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="ptable">
              <thead>
                <tr>
                  <th scope="col">Added</th>
                  <th scope="col">Pair</th>
                  <th scope="col">Note</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Liquidity
                  </th>
                  <th scope="col">Risk</th>
                  <th scope="col">Lifecycle</th>
                  <th scope="col">Status</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Remove
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="ptable-num" title={absoluteTime(item.created_at)}>
                      {relativeAge(item.created_at)}
                    </td>
                    <td>
                      <span className="pair-id">
                        <Link className="pair-symbols" to={`/pairs/${item.pair_id}`}>
                          <PairSymbols pair={item.pair} />
                        </Link>
                        <span className="pair-address">
                          {shortAddress(item.pair.address, 10, 6)}
                        </span>
                      </span>
                    </td>
                    <td>
                      {item.notes ? (
                        item.notes
                      ) : (
                        <span className="token-unknown">No note</span>
                      )}
                    </td>
                    <td className="ptable-num">{formatEth(item.pair.liquidity_eth)}</td>
                    <td>
                      <RiskBadge risk={item.pair.risk} />
                    </td>
                    <td>
                      <span className="chip" data-tone={item.pair.lifecycle_status ? 'neutral' : 'muted'}>
                        {item.pair.lifecycle_status ?? 'not classified'}
                      </span>
                    </td>
                    <td>
                      <span className="chip" data-tone={item.pair.status}>
                        {item.pair.status}
                      </span>
                    </td>
                    <td className="ptable-actions">
                      <button
                        className="btn-mini"
                        type="button"
                        disabled={busyId === item.id}
                        onClick={() => void remove(item)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
